import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, BookOpen, Book as BookIcon } from 'lucide-react';
import { StudyLife, loadComposite } from './studyLife';
import { Binding, preloadBinding } from './SpellBook';
import { CoverMark } from './GiltCover';
import { drawSign, fontMask, type SignStyle } from './studySign';
import { drawWand } from './studyWand';

/*
 * The pixel skin's Home Base: the study. The room and desk are Merino Kobo's "Wizard's Study"
 * (public/assets/study; licensed for use and modification in the app, not for redistribution as files).
 * The art is 640 × 360 at its own pixel size; the areas are drawn into it at that same size and in its
 * palette, as a stack of books on the desk. Picking one lifts it up to face the reader and opens it; a
 * second opens beside it. Closing sets it back on the stack.
 */

export interface ShelfBook {
  key: string;
  title: string;
  /** Leather for the binding: red, blue, green, purple, brown or grey */
  color: string;
  locked?: string;
  /** The open book; `closed` asks it to shut its cover */
  render: (closed: boolean) => ReactNode;
}

const ART = { w: 640, h: 360 };
const BOOK = { w: 1047, h: 624 };                 // the open Book as drawn
const VIS = { x: 129, y: 105, w: 807, h: 492 };   // the visible book inside that frame
const TABS = 66;                                   // bookmarks overhang the right edge
const CLOSE_MS = 800, LIFT_MS = 520;

/* Colours from the study art: ink, the amber wood ramp, the warm highlight */
const C = { ink: 'rgb(25,8,1)', dark: 'rgb(72,35,5)', wood: 'rgb(142,77,7)', hi: 'rgb(246,216,115)', gold: 'rgb(212,131,1)' };
/* Leathers as they look under the study's candlelight: [shade, base, highlight] */
/* Leathers as they look under the study's candlelight: [shade, base, highlight]. The open books take
   their colours from these, as candlelight renders them on the desk (SpellBook's LEATHERS). */
const LEATHER: Record<string, [string, string, string]> = {
  red: ['rgb(110,30,4)', 'rgb(155,48,5)', 'rgb(198,78,22)'],
  blue: ['rgb(40,46,66)', 'rgb(62,72,98)', 'rgb(96,108,132)'],
  green: ['rgb(56,64,18)', 'rgb(88,100,32)', 'rgb(128,138,52)'],
  purple: ['rgb(66,30,44)', 'rgb(98,46,62)', 'rgb(136,74,84)'],
  brown: ['rgb(86,52,12)', 'rgb(142,77,7)', 'rgb(185,107,6)'],
  grey: ['rgb(60,58,46)', 'rgb(97,101,81)', 'rgb(133,136,108)'],
};
/* Two stacks of tomes in the middle of the desk, where the art's own open book was painted out (art pixels) */
const STACK = { x: [204, 298], bottom: 318, w: 90, h: 20 }; // tall enough for a blackletter title on the spine
const TOP = 5, TAIL = 5; // the top book's cover seen from above; room under each for a ribbon

/** Fit the art to cover the window, keeping its bottom (the desk) in view */
function useFit() {
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => { const r = () => setVp({ w: window.innerWidth, h: window.innerHeight }); window.addEventListener('resize', r); return () => window.removeEventListener('resize', r); }, []);
  // Cover the window; the art's pixels are fine enough that a fractional scale stays even to the eye
  const k = Math.max(vp.w / ART.w, vp.h / ART.h);
  const ox = Math.round((vp.w - ART.w * k) / 2), oy = vp.h - ART.h * k;
  return { k, ox, oy, vw: vp.w, vh: vp.h };
}

const chromeBtn: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
  fontFamily: '"Silkscreen", monospace', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.hi, background: C.dark, boxShadow: `0 0 0 2px ${C.ink}`,
};

/* A tome lying on the desk, spine toward the reader, at the art's own pixel size: boards, grained
   leather, raised bands, gilt rules, a recessed gilt-bordered title plate, the page block with its
   leaves, a ribbon marker; the top book of a stack shows its cover; locked books are strapped in iron. */
const SHADOW = 'rgb(72,35,5)';
/* The room's darkest brown: the painted objects are edged in it, never in black */
const EDGE = 'rgb(58,33,16)';
const STUDY = '/assets/study/study-hd8.png';
const IRON = ['rgb(52,52,48)', 'rgb(96,98,90)', 'rgb(150,152,140)'];
/** The title plate, cut long enough for the title (art pixels within the tome) */
function plateOf(title: string, locked: boolean) {
  const w = STACK.w, h = STACK.h, name = fontMask(title, '500 13px "Grenze Gotisch"', 140, 0.5); // gilt blackletter, like the covers
  // Centred on the spine, clear of the gilt rules at each end
  const half = Math.min(Math.floor(w / 2) - 6, Math.max(20, Math.ceil(name.w / 2) + 3));
  const p0 = Math.round(w / 2) - half, p1 = Math.round(w / 2) + half - 1;
  return { p0, p1, q0: 3, q1: h - 4, name };
}

/** Gilt title pressed into the plate, drawn after the softening so the letters stay legible */
function gildTitle(c: HTMLCanvasElement, title: string, locked: boolean) {
  const x = c.getContext('2d')!, { p0, p1, q0, q1, name } = plateOf(title, locked);
  const nx = p0 + 1 + Math.round((p1 - p0 - 1 - name.w) / 2), ny = q0 + 1 + Math.round((q1 - q0 - 2 - name.h) / 2); // the context is still shifted down by TOP
  for (let yy = 0; yy < name.h; yy++) for (let xx = 0; xx < name.w; xx++) {
    if (!name.on(xx, yy)) continue;
    // Aged gilt shaded by the candlelight: brighter toward the lit end of the word, top edges catching the light
    // Where the light catches the word differs book to book
    const at = (([...title].reduce((a, ch) => a * 31 + ch.charCodeAt(0), 7) % 997) / 997) * 0.8 + 0.1;
    const band = Math.min(3, Math.floor(Math.abs(xx / Math.max(1, name.w) - at) * 4.5) + (name.on(xx, yy - 1) ? 1 : 0));
    x.fillStyle = ['rgb(232,200,118)', 'rgb(204,160,74)', 'rgb(176,128,52)', 'rgb(140,98,40)'][band]; x.fillRect(nx + xx, ny + yy, 1, 1);
    if (!name.on(xx, yy + 1)) { x.fillStyle = 'rgb(57,31,17)'; x.fillRect(nx + xx, ny + yy + 1, 1, 1); }
  }
}

function tomeArt(color: string, locked: boolean, top: boolean, ribbon: string | null, title: string) {
  const w = STACK.w, h = STACK.h;
  const c = document.createElement('canvas'); c.width = w + 3; c.height = TOP + h + TAIL;
  const x = c.getContext('2d')!;
  x.translate(0, TOP);
  let seed = [...color].reduce((a, ch) => a * 31 + ch.charCodeAt(0), 7) + (locked ? 13 : 0);
  const R = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  const f = (X: number, Y: number, W: number, H: number, col: string) => { x.fillStyle = col; x.fillRect(X, Y, W, H); };
  const [shade, base, hl] = LEATHER[color] ?? LEATHER.brown;
  const { p0, p1, q0, q1 } = plateOf(title, locked);
  // Shadow on the desk, then the outline
  f(2, 2, w, h, SHADOW);
  f(0, 0, w, h, EDGE);
  // Boards: covers proud of the spine above and below
  f(1, 1, w - 2, 1, hl); f(1, h - 2, w - 2, 1, shade);
  // Spine leather: rounded (lit above, shaded below) with grain
  for (let yy = 2; yy < h - 2; yy++) for (let xx = 1; xx < w - 1; xx++) {
    const t = (yy - 2) / (h - 5), n = R();
    f(xx, yy, 1, 1, n < 0.07 ? shade : n > 0.96 ? hl : t < 0.2 ? hl : t > 0.78 ? shade : base);
  }
  f(1, 2, 1, h - 4, shade); f(w - 2, 2, 1, h - 4, shade);
  // Raised bands near each end (mirrored), each a ridge catching the light
  for (const bx of [4, w - 8]) { f(bx, 1, 1, h - 2, EDGE); f(bx + 1, 1, 1, h - 2, hl); f(bx + 2, 1, 1, h - 2, base); f(bx + 3, 1, 1, h - 2, EDGE); }
  // Gilt double rules
  for (const gx of [10, w - 13].filter(g => g + 2 < p0 || g > p1)) { f(gx, 2, 1, h - 4, C.gold); f(gx + 2, 2, 1, h - 4, C.gold); f(gx + 1, Math.round(h / 2) - 1, 1, 2, C.hi); }
  // Recessed title plate with a gilt border and corner studs

  f(p0, q0, p1 - p0, q1 - q0, shade); f(p0 + 1, q0 + 1, p1 - p0 - 1, 1, EDGE);
  f(p0, q0, p1 - p0 + 1, 1, C.gold); f(p0, q1, p1 - p0 + 1, 1, C.gold); f(p0, q0, 1, q1 - q0, C.gold); f(p1, q0, 1, q1 - q0, C.gold);
  for (const [cx, cy] of [[p0, q0], [p1, q0], [p0, q1], [p1, q1]]) f(cx, cy, 1, 1, C.hi);
  // The top book shows its cover from above: leather, a gilt frame, corner fleurons
  if (top) {
    f(1, -TOP, w - 2, TOP, EDGE);
    for (let r = 0; r < TOP - 1; r++) f(2 + (TOP - 2 - r), -TOP + 1 + r, w - 4 - (TOP - 2 - r), 1, r === TOP - 2 ? hl : base);
    f(6, -TOP + 2, w - 12, 1, C.gold);
    for (const cx of [7, w - 9]) f(cx, -TOP + 2, 2, 1, C.hi);
  }
  // A ribbon marker hanging out from between the boards
  if (ribbon) { const rx = w - 7; f(rx, h - 1, 3, TAIL - 1, ribbon); f(rx, h + TAIL - 2, 1, 1, 'rgba(0,0,0,0)'); x.clearRect(rx + 1, h + TAIL - 2, 1, 1); f(rx + 2, h - 1, 1, TAIL - 2, EDGE); }
  if (locked) {
    // An iron strap round the book, riveted, with a hasp and a padlock
    const sx = 8;
    f(sx, -1, 6, h + 2, IRON[0]); f(sx + 1, -1, 4, h + 2, IRON[1]); f(sx + 2, -1, 1, h + 2, IRON[2]);
    for (const ry of [1, h - 2]) f(sx + 3, ry, 1, 1, EDGE);
    const my = Math.round(h / 2);
    f(sx - 1, my - 3, 8, 7, EDGE); f(sx, my - 2, 6, 5, 'rgb(170,120,30)'); f(sx + 1, my - 2, 4, 1, C.hi); f(sx + 2, my, 2, 2, EDGE);
  }
  return c;
}

/* The study art's own colours. Tomes are lit with its amber candlelight and then snapped to this
   palette, so they're painted from the same box of paints as the room around them. */
let palette: Promise<number[][]> | null = null;
function studyPalette() {
  palette ??= new Promise(res => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
      const x = c.getContext('2d')!; x.drawImage(img, 0, 0);
      const d = x.getImageData(0, 0, c.width, c.height).data, seen = new Set<number>(), out: number[][] = [];
      for (let i = 0; i < d.length; i += 4) { const key = (d[i] << 16) | (d[i + 1] << 8) | d[i + 2]; if (!seen.has(key)) { seen.add(key); out.push([d[i], d[i + 1], d[i + 2]]); } }
      res(out);
    };
    img.onerror = () => res([]);
    img.src = STUDY;
  });
  return palette;
}
function paintInStudy(c: HTMLCanvasElement, pal: number[][], candlelit = true, snap = true) {
  if (snap && !pal.length) return;
  const x = c.getContext('2d')!, im = x.getImageData(0, 0, c.width, c.height), d = im.data;
  // Soften like the painted objects around it: each pixel takes a little of its neighbours
  const src = new Uint8ClampedArray(d), W = c.width;
  for (let i = 0; i < d.length; i += 4) {
    if (!src[i + 3]) continue;
    const n = [i - 4, i + 4, i - W * 4, i + W * 4].filter(j => j >= 0 && j < d.length && src[j + 3]);
    for (let ch = 0; ch < 3; ch++) d[i + ch] = src[i + ch] * 0.7 + (n.reduce((s, j) => s + src[j + ch], 0) / Math.max(1, n.length)) * 0.3;
  }
  for (let i = 0; i < d.length; i += 4) {
    if (!d[i + 3]) continue;
    // Candlelight: warm, a touch brighter toward the top of each book
    const row = Math.floor(i / 4 / c.width), lift = 1.06 - (row / c.height) * 0.16;
    if (!candlelit && !snap) continue;
    const r = d[i] * 1.03 * lift, g = d[i + 1] * 0.96 * lift, b = d[i + 2] * 0.86 * lift;
    if (!snap) { d[i] = Math.min(255, r); d[i + 1] = Math.min(255, g); d[i + 2] = Math.min(255, b); continue; }
    let best = pal[0], bd = Infinity;
    for (const p of pal) { const e = (p[0] - r) ** 2 + (p[1] - g) ** 2 + (p[2] - b) ** 2; if (e < bd) { bd = e; best = p; } }
    d[i] = best[0]; d[i + 1] = best[1]; d[i + 2] = best[2];
  }
  x.putImageData(im, 0, 0);
}

function Tome({ color, locked, top, ribbon, title, k }: { color: string; locked: boolean; top: boolean; ribbon: string | null; title: string; k: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    let alive = true;
    Promise.all([studyPalette(), document.fonts.load('500 13px "Grenze Gotisch"')]).then(([pal]) => {
      if (!alive) return;
      const art = tomeArt(color, locked, top, ribbon, title);
      paintInStudy(art, pal);
      gildTitle(art, title, locked);
      cv.width = art.width; cv.height = art.height;
      cv.getContext('2d')!.drawImage(art, 0, 0);
    });
    return () => { alive = false; };
  }, [color, locked, top, ribbon, title]);
  return <canvas ref={ref} style={{ position: 'absolute', left: 0, top: -TOP * k, width: (STACK.w + 3) * k, height: (TOP + STACK.h + TAIL) * k, imageRendering: 'pixelated', filter: locked ? 'brightness(0.82)' : undefined, pointerEvents: 'none' }} />;
}

/** The art's living parts: one 640 × 360 canvas laid exactly over it */
function LifeLayer({ style }: { style: CSSProperties }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let raf = 0, last = 0, alive = true, life: StudyLife | null = null;
    loadComposite(STUDY).then(d => { if (alive) life = new StudyLife(d); }).catch(() => undefined);
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (now - last < 110 || !life) return;
      last = now;
      const ctx = ref.current?.getContext('2d');
      if (ctx) life.draw(ctx, now / 1000);
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, []);
  return <canvas ref={ref} width={ART.w} height={ART.h} style={style} />;
}

/** The elder wand on the desk, drawn at the tomes' resolution */
function WandLayer({ style }: { style: CSSProperties }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let alive = true;
    const x = ref.current?.getContext('2d');
    if (alive && x) drawWand(x);
    return () => { alive = false; };
  }, []);
  return <canvas ref={ref} width={ART.w} height={ART.h} style={style} />;
}

/** The study's name drawn into the art (chalk on the board, or a hanging carved sign) */
function SignLayer({ style, sign, who }: { style: CSSProperties; sign: SignStyle; who: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let alive = true, timer = 0;
    Promise.all([document.fonts.load('700 16px Silkscreen'), document.fonts.load('8px Silkscreen'), document.fonts.load('500 26px "Grenze Gotisch"'), document.fonts.load('400 15px "Grenze Gotisch"'), loadComposite(STUDY).catch(() => null)]).then(([, , , , art]) => {
      const tick = () => { const x = ref.current?.getContext('2d'); if (alive && x) drawSign(x, sign, who, performance.now() / 1000, art); };
      tick();
      if (sign === 'wood') timer = window.setInterval(tick, 400);
    });
    return () => { alive = false; clearInterval(timer); };
  }, [sign, who]);
  return <canvas ref={ref} width={ART.w} height={ART.h} style={style} />;
}

export function Library({ books, who = 'traveler' }: { books: ShelfBook[]; who?: string }) {
  // The study's name is chalked on the board; ?sign=wood or ?sign=off (with a switch) shows the alternatives
  const [sign, setSign] = useState<SignStyle | null>(() => { const v = new URLSearchParams(location.search).get('sign'); return v === 'wood' ? 'wood' : v === 'off' ? null : 'chalk'; });
  const { k, ox, oy, vw, vh } = useFit();
  // Recolour every book's frames up front, so opening one never waits or flickers
  useEffect(() => { books.forEach(b => { void preloadBinding(b.color); }); }, [books.map(b => b.color).join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const [open, setOpen] = useState<string[]>([]);
  const [closing, setClosing] = useState<string | null>(null);
  // Books shut where they float (cover closed, still in front of you); clicking the cover opens it again
  const [shut, setShut] = useState<Set<string>>(new Set());
  const toggleShut = (key: string) => setShut(o => { const n = new Set(o); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const [lifting, setLifting] = useState<string | null>(null);
  // Open books can be pushed around the room: grab any bare part of the book and drag
  const [moved, setMoved] = useState<Record<string, { x: number; y: number }>>({});
  const [dragging, setDragging] = useState<string | null>(null);
  const dragged = useRef(false); // a drag that ends over a shut cover shouldn't also open it
  const grab = (key: string) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button, a, input, textarea, select, label, [contenteditable], .sb-scroll, canvas')) return;
    const el = e.currentTarget, sx = e.clientX, sy = e.clientY, from = moved[key] ?? { x: 0, y: 0 };
    let live = false;
    const move = (m: PointerEvent) => {
      const dx = m.clientX - sx, dy = m.clientY - sy;
      if (!live && Math.hypot(dx, dy) < 6) return;
      if (!live) { live = true; dragged.current = true; setDragging(key); el.setPointerCapture(e.pointerId); window.getSelection()?.removeAllRanges(); }
      setMoved(o => ({ ...o, [key]: { x: from.x + dx, y: from.y + dy } }));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); setDragging(null); setTimeout(() => { dragged.current = false; }, 0); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const later = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const byKey = (key: string) => books.find(b => b.key === key)!;
  const px = (n: number) => Math.round(n * k); // art pixels → screen pixels

  // Open books float in front of the room: one centred, or two side by side, as large as fits
  const n = Math.max(1, open.length);
  const span = VIS.w + TABS;
  const scale = Math.min(1.15, (vh * 0.8) / VIS.h, (vw * 0.94 - 24 * (n - 1)) / (span * n));
  const total = span * scale * n + 24 * (n - 1);
  const leftOf = (slot: number) => (vw - total) / 2 + slot * (span * scale + 24) - VIS.x * scale;
  const topOf = (vh - VIS.h * scale) / 2 - VIS.y * scale + 20;
  // Double-clicking an open book brings it forward at full size; double-clicking again sets it back beside the other
  const [big, setBig] = useState<string | null>(null);
  const bigScale = Math.min(1.6, (vh * 0.84) / VIS.h, (vw * 0.95) / span);
  const bigLeft = (vw - span * bigScale) / 2 - VIS.x * bigScale, bigTop = (vh - VIS.h * bigScale) / 2 - VIS.y * bigScale + 10;

  const pick = (b: ShelfBook) => {
    if (closing || lifting || b.locked || open.includes(b.key)) return;
    setLifting(b.key);
    later(() => { setLifting(null); setOpen(o => [...(o.length >= 2 ? o.slice(1) : o), b.key]); }, LIFT_MS);
  };
  const close = (key: string) => {
    if (closing) return;
    setClosing(key);
    later(() => { setClosing(null); setBig(b => (b === key ? null : b)); setOpen(o => o.filter(x => x !== key)); setMoved(o => { const n = { ...o }; delete n[key]; return n; }); }, CLOSE_MS);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && open.length) close(open[open.length - 1]); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const stack = books.filter(b => !open.includes(b.key));
  const art: CSSProperties = { position: 'absolute', left: ox, top: oy, width: ART.w * k, height: ART.h * k, maxWidth: 'none', imageRendering: 'pixelated', pointerEvents: 'none' };

  return (
    <>
      <style>{`
        @keyframes study-flicker { 0%, 100% { opacity: 0.55; } 25% { opacity: 0.8; } 50% { opacity: 0.5; } 75% { opacity: 0.72; } }
        @keyframes study-gem { 0%, 100% { opacity: 0.25; } 50% { opacity: 0.7; } }
        @keyframes study-lift { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-${px(28)}px); opacity: 0; } }
        @keyframes study-dim { from { opacity: 0; } to { opacity: 1; } }
        .study-spine { transition: transform 0.12s steps(2); }
        .study-spine:hover { transform: translateX(${px(2)}px); }
        .study-spine:hover .study-glow { opacity: 1; }
        ${sign ? '.hub-header > :nth-child(-n+2) { visibility: hidden; }' : ''}
      `}</style>

      {/* The room, its light, the desk in front */}
      <div style={{ position: 'fixed', inset: 0, zIndex: -1, overflow: 'clip', background: C.ink }}>
        <img src={STUDY} alt="" draggable={false} style={art} />
        {/* Flames, bubbles and embers drawn over the art at its own pixel size */}
        {/* The chalked board sits under the living layer so the board candle's flame still flickers */}
        {sign && <SignLayer style={art} sign={sign} who={who} />}
        <WandLayer style={art} />
        <LifeLayer style={art} />
        {/* The blue gem's pulse */}
        <div style={{ position: 'absolute', left: ox + 178 * k, top: oy + 279 * k, width: px(40), height: px(40), transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(150,180,255,0.6), transparent 70%)', mixBlendMode: 'screen', animation: 'study-gem 3.2s ease-in-out infinite', pointerEvents: 'none' }} />
      </div>

      {createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 30, pointerEvents: 'none' }}>
          {/* Reading: the room dims, the books float up to face the reader */}
          {open.length > 0 && (
            <div onClick={() => close(open[open.length - 1])} style={{ position: 'absolute', inset: 0, background: 'rgba(12,5,1,0.55)', pointerEvents: 'auto', animation: 'study-dim 0.3s ease-out' }} />
          )}
          {/* The stack stays lit and within reach above the dimmed room, beneath the open books */}
          {/* The stack of area books on the desk, spines toward the reader */}
          {stack.map((b, i) => {
            const locked = Boolean(b.locked);
            // Books above one that's been lifted out settle down onto the one below
            const colOf = (o: ShelfBook) => (books.indexOf(o) < 3 ? 0 : 1);
            const pos = books.indexOf(b), col = colOf(b), row = stack.filter(o => colOf(o) === col && books.indexOf(o) < pos).length;
            const x = STACK.x[col] + [0, 3, -2][row] + (col ? -2 : 0), y = STACK.bottom - (row + 1) * STACK.h;
            const top = !stack.some(o => colOf(o) === col && books.indexOf(o) > pos);
            const ribbon = [C.hi, 'rgb(170,30,20)', null, 'rgb(70,110,60)', null, C.hi][pos % 6];
            return (
              <button key={b.key} type="button" className={locked ? undefined : 'study-spine'} onClick={() => pick(b)}
                title={b.locked ?? `Open ${b.title}`} aria-label={b.locked ? `${b.title}: ${b.locked}` : `Open ${b.title}`}
                style={{
                  position: 'absolute', left: ox + x * k, top: oy + y * k, width: px(STACK.w), height: px(STACK.h), padding: 0, border: 'none', background: 'none',
                  pointerEvents: 'auto', cursor: locked ? 'not-allowed' : 'pointer', overflow: 'visible',
                  transition: 'top 0.32s cubic-bezier(.55,0,.8,.4), left 0.32s, transform 0.12s steps(2)',
                  animation: lifting === b.key ? `study-lift ${LIFT_MS}ms ease-in forwards` : undefined,
                }}>
                <Tome color={b.color} locked={locked} top={top} ribbon={ribbon} title={b.title} k={k} />
                <span className="study-glow" style={{ position: 'absolute', inset: -px(2), opacity: 0, transition: 'opacity 0.15s', boxShadow: `0 0 ${px(3)}px ${px(1)}px rgba(255,210,110,0.75)`, pointerEvents: 'none' }} />
              </button>
            );
          })}
          {new URLSearchParams(location.search).has('sign') && (
            <div style={{ position: 'absolute', left: 16, bottom: 16, display: 'flex', gap: 6, pointerEvents: 'auto' }}>
              {([['off', null], ['chalk', 'chalk'], ['wood', 'wood']] as const).map(([t, v]) => (
                <button key={t} type="button" onClick={() => setSign(v)} style={{ ...chromeBtn, background: sign === v ? C.gold : C.dark, color: sign === v ? C.ink : C.hi }}>Sign: {t}</button>
              ))}
            </div>
          )}
          {open.map((key, slot) => { const s = big === key ? bigScale : scale; return (
            <div key={key} onPointerDown={grab(key)} onDoubleClick={e => { if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [contenteditable]')) return; setBig(b => (b === key ? null : key)); }} style={{ position: 'absolute', left: big === key ? bigLeft : leftOf(slot) + (moved[key]?.x ?? 0), top: big === key ? bigTop : topOf + (moved[key]?.y ?? 0), width: BOOK.w * s, height: BOOK.h * s, pointerEvents: 'auto',
              zIndex: big === key ? 3 : dragging === key ? 2 : 1, cursor: dragging === key ? 'grabbing' : undefined, userSelect: dragging ? 'none' : undefined,
              transition: dragging === key ? 'none' : 'left 0.45s cubic-bezier(.3,.8,.25,1), top 0.45s, width 0.45s cubic-bezier(.3,.8,.25,1), height 0.45s', filter: `drop-shadow(0 ${px(4)}px ${px(4)}px rgba(0,0,0,0.6))` }}>
              <div style={{ position: 'absolute', left: 0, top: 0, width: BOOK.w, height: BOOK.h, transform: `scale(${s})`, transformOrigin: 'top left' }}>
                <Binding.Provider value={byKey(key).color}><CoverMark.Provider value={key}>{byKey(key).render(closing === key || shut.has(key))}</CoverMark.Provider></Binding.Provider>
              </div>
              {/* A shut book's cover opens it again */}
              {shut.has(key) && closing !== key && (
                <div role="button" tabIndex={0} aria-label={`Open ${byKey(key).title}`}
                  onClick={() => { if (!dragged.current) toggleShut(key); }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleShut(key); }}
                  style={{ position: 'absolute', left: '28%', width: '44%', top: VIS.y * s, height: VIS.h * s, cursor: dragging === key ? 'grabbing' : 'grab' }} />
              )}
              <div style={{ position: 'absolute', left: '50%', top: (VIS.y + VIS.h) * s + 14, transform: 'translateX(-50%)', display: 'flex', gap: 8 }}>
                <button type="button" style={chromeBtn} onClick={() => toggleShut(key)}>{shut.has(key) ? <><BookOpen size={13} /> Open book</> : <><BookIcon size={13} /> Close book</>}</button>
                <button type="button" style={chromeBtn} onClick={() => { setShut(o => { const n = new Set(o); n.delete(key); return n; }); close(key); }}><X size={13} /> Back on the desk</button>
              </div>
            </div>
          ); })}
        </div>,
        document.body,
      )}
    </>
  );
}
